// The security controls on /profile: the password, and the authenticator
// app (connect - QR, one code, recovery codes shown exactly once - and
// disconnect, where a code proves possession). Each control is drawn only
// where it can work: an account with no password has nothing to change, and
// a mailed link needs the firm's mail. After a mutation the section keeps
// its own truth - the context catches up on the next /me probe.

import { useRef, useState, type FormEvent } from "react";
import {
  changePassword,
  confirmMfaSetup,
  disableMfa,
  requestStaffPasswordLink,
  startMfaSetup,
  MIN_PASSWORD_LENGTH,
  REQUEST_FAILED,
  type MfaSetupStart,
} from "../../api/auth";
import { usePasswordOptions } from "../../hooks/usePasswordOptions";
import { fmtDate } from "../../lib/dates";
import { useCurrentUser } from "../auth/CurrentUserContext";
import { Dialog } from "../chrome/Dialog";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";

type Ceremony = "closed" | "setup" | "codes" | "disable" | "password";

const LINK_SENT = "Check your email - the link is good for one use.";
const LINK_REFUSED: Record<string, string> = {
  mail_unavailable: "The firm's email is not set up yet, so no link could be sent.",
  rate_limited: "Too many requests. Try again in a few minutes.",
  request_failed: REQUEST_FAILED,
};

const asGroups = (secret: string) => secret.replace(/(.{4})/g, "$1 ").trim();
const asPair = (code: string) => `${code.slice(0, 5)}-${code.slice(5)}`;

export function SecuritySection() {
  const user = useCurrentUser();
  const options = usePasswordOptions();
  const [override, setOverride] = useState<string | null | undefined>(undefined);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [changed, setChanged] = useState(false);
  const [linkNote, setLinkNote] = useState<string | null>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [ceremony, setCeremony] = useState<Ceremony>("closed");
  const [setup, setSetup] = useState<Extract<MfaSetupStart, { ok: true }> | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  // Every close or re-open advances the sequence, so a slow mint that
  // lands late cannot show a secret the server has already superseded.
  const setupSeq = useRef(0);

  const enabledAt = override !== undefined ? override : (user?.mfaEnabledAt ?? null);

  function close() {
    setupSeq.current++;
    setCeremony("closed");
    setSetup(null);
    setCode("");
    setCurrent("");
    setNext("");
    setRecoveryCodes([]);
    setError(null);
    setCopied(false);
  }

  async function openSetup() {
    setCeremony("setup");
    setSetup(null);
    setError(null);
    setCode("");
    const seq = ++setupSeq.current;
    try {
      const result = await startMfaSetup();
      if (seq !== setupSeq.current) return;
      if (result.ok) setSetup(result);
      else setError(result.message);
    } catch {
      if (seq === setupSeq.current) setError(REQUEST_FAILED);
    }
  }

  function openPassword() {
    setCeremony("password");
    setError(null);
    setCurrent("");
    setNext("");
  }

  function openDisable() {
    setCeremony("disable");
    setError(null);
    setCode("");
  }

  async function onConfirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await confirmMfaSetup(code);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setOverride(new Date().toISOString());
      setRecoveryCodes(result.recoveryCodes);
      setCode("");
      setCeremony("codes");
    } catch {
      setError(REQUEST_FAILED);
    } finally {
      setBusy(false);
    }
  }

  async function onDisable(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await disableMfa(code);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setOverride(null);
      close();
    } catch {
      setError(REQUEST_FAILED);
    } finally {
      setBusy(false);
    }
  }

  async function onChangePassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await changePassword(current, next);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      setChanged(true);
      close();
    } catch {
      setError(REQUEST_FAILED);
    } finally {
      setBusy(false);
    }
  }

  async function onMailPasswordLink() {
    if (!user) return;
    setLinkBusy(true);
    setLinkNote(null);
    try {
      const result = await requestStaffPasswordLink(user.email);
      setLinkNote(result.ok ? LINK_SENT : (LINK_REFUSED[result.reason] ?? REQUEST_FAILED));
    } catch {
      setLinkNote(REQUEST_FAILED);
    } finally {
      setLinkBusy(false);
    }
  }

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.map(asPair).join("\n"));
      setCopied(true);
    } catch {
      setError("Could not copy. Select the codes and copy them by hand.");
    }
  }

  return (
    <section className="cc-card profile-section">
      <h3>Security</h3>

      {!user || options.isLoading ? (
        <SkeletonGroup>
          <Skeleton />
          <Skeleton width={180} />
        </SkeletonGroup>
      ) : options.isError || !options.data ? (
        <p className="profile-row-sub" role="status">
          {REQUEST_FAILED}
        </p>
      ) : (
        <>
          <div className="profile-row">
            <span>
              <span className="profile-row-title">Password</span>
              <span className="profile-row-sub">
                {options.data.hasPassword
                  ? changed
                    ? "Password changed. Every other session was signed out."
                    : "You sign in with your email and a password."
                  : options.data.canSetByMail
                    ? (linkNote ??
                      "This account signs in with Google. A password lets you sign in without it, and an authenticator app protects password sign-in.")
                    : "This account signs in with Google. Setting a password goes by email, and the firm has not set up email yet."}
              </span>
            </span>
            {options.data.hasPassword ? (
              <button type="button" className="profile-btn" onClick={openPassword}>
                Change password
              </button>
            ) : options.data.canSetByMail ? (
              <button
                type="button"
                className="profile-btn"
                onClick={onMailPasswordLink}
                disabled={linkBusy}
              >
                {linkBusy ? "Sending..." : "Email a link to set a password"}
              </button>
            ) : null}
          </div>

          {/* A factor already connected stays manageable whatever the
              password state, or it could never be turned off again. */}
          {options.data.hasPassword || enabledAt ? (
            <div className="profile-row">
              <span>
                <span className="profile-row-title">Authenticator app</span>
                <span className="profile-row-sub">
                  {enabledAt
                    ? options.data.hasPassword
                      ? `Connected ${fmtDate(enabledAt, "")}`
                      : `Connected ${fmtDate(enabledAt, "")}. Signing in with Google does not ask for a code.`
                    : "One-time codes from an app protect your password sign-in."}
                </span>
              </span>
              {enabledAt ? (
                <button type="button" className="profile-btn" onClick={openDisable}>
                  Disconnect
                </button>
              ) : (
                <button type="button" className="profile-btn" onClick={openSetup}>
                  Connect an authenticator app
                </button>
              )}
            </div>
          ) : null}
        </>
      )}

      <Dialog open={ceremony === "password"} onClose={close} title="Change password" locked={busy}>
        {error ? (
          <div role="alert" className="profile-error">
            {error}
          </div>
        ) : null}
        <form className="profile-mfa-form profile-password-form" onSubmit={onChangePassword}>
          <label>
            Current password
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
            />
          </label>
          <label>
            New password
            <input
              type="password"
              autoComplete="new-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
            />
          </label>
          <p className="profile-row-sub">
            At least {MIN_PASSWORD_LENGTH} characters. Every other session is signed out.
          </p>
          <button type="submit" className="profile-btn" disabled={busy}>
            {busy ? "Saving..." : "Save new password"}
          </button>
        </form>
      </Dialog>

      <Dialog
        open={ceremony === "setup"}
        onClose={close}
        title="Connect an authenticator app"
        locked={busy}
      >
        {error ? (
          <div role="alert" className="profile-error">
            {error}
          </div>
        ) : null}
        {!setup && !error ? (
          <SkeletonGroup>
            <Skeleton height={180} />
            <Skeleton width={220} />
          </SkeletonGroup>
        ) : null}
        {setup ? (
          <>
            <p className="profile-row-sub">
              Scan the QR code with your authenticator app, or type the key in by hand. Then enter
              the 6-digit code the app shows.
            </p>
            <img
              className="profile-mfa-qr"
              src={setup.qrDataUri}
              alt="QR code for the authenticator app"
            />
            <div className="profile-mfa-secret profile-mono">{asGroups(setup.secret)}</div>
            <form className="profile-mfa-form" onSubmit={onConfirm}>
              <label>
                Code from the app
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </label>
              <button type="submit" className="profile-btn" disabled={busy}>
                {busy ? "Checking..." : "Turn on"}
              </button>
            </form>
          </>
        ) : null}
      </Dialog>

      {/* locked: Escape or a stray scrim click must not destroy the
          one-time payout - the X and the button below are the exits. */}
      <Dialog open={ceremony === "codes"} onClose={close} title="Recovery codes" locked>
        <p className="profile-row-sub">
          Each code signs you in once if the phone is lost. Save them now - they are shown only this
          once.
        </p>
        <ul className="profile-mfa-codes profile-mono">
          {recoveryCodes.map((c) => (
            <li key={c}>{asPair(c)}</li>
          ))}
        </ul>
        {error ? (
          <div role="alert" className="profile-error">
            {error}
          </div>
        ) : null}
        <div className="profile-mfa-actions">
          <button type="button" className="profile-btn" onClick={onCopy}>
            {copied ? "Copied" : "Copy all"}
          </button>
          <button type="button" className="profile-btn" onClick={close}>
            I saved these codes
          </button>
        </div>
      </Dialog>

      <Dialog
        open={ceremony === "disable"}
        onClose={close}
        title="Disconnect the authenticator app"
        locked={busy}
      >
        {error ? (
          <div role="alert" className="profile-error">
            {error}
          </div>
        ) : null}
        <p className="profile-row-sub">
          Enter a current code from the app, or one of your recovery codes, to prove it is you.
        </p>
        <form className="profile-mfa-form" onSubmit={onDisable}>
          <label>
            Code
            <input
              type="text"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
          </label>
          <button type="submit" className="profile-btn" disabled={busy}>
            {busy ? "Checking..." : "Disconnect the app"}
          </button>
        </form>
      </Dialog>
    </section>
  );
}
